import React, { useState } from 'react';
import Alert from '../components/Alert';
import { apiClient } from '../services/api';

const SettingsPage: React.FC = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);

      // アカウント停止APIを呼び出し (CSRFトークン付き)
      const response = await apiClient.postRaw<{
        status: string;
        redirect_url: string;
        deleted_items: Record<string, number>;
        log_id?: string;
        error?: string;
      }>('/api/account/suspend', { confirm: true });

      // 成功時のみリダイレクト
      if (response.status === 200 && response.data.status === 'completed') {
        // バックエンドから受け取ったリダイレクトURLに遷移
        window.location.href = response.data.redirect_url;
      } else {
        // 失敗時はエラーメッセージを表示
        const errorMsg = response.data.error || 'アカウント停止に失敗しました';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('アカウント停止エラー:', error);

      // バックエンドから返されたエラーメッセージを優先的に使用
      let errorMessage = 'アカウントの停止に失敗しました。時間をおいて再度お試しください。';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const err = error as { response?: { data?: { error?: string } } };
        if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }
      }

      // エラーメッセージを表示
      alert(errorMessage);
      setIsDeleting(false);
      setShowDialog(false);
    }
    // finally句を削除 (リダイレクト成功時は実行されないため)
  };

  return (
    <div className="max-w-4xl mx-auto w-full py-10 px-4">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">設定</h1>
        <p className="mt-2 text-sm text-gray-600">
          アカウントとサービスの設定を管理します
        </p>
      </header>

      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">利用停止</h2>
        <p className="text-sm text-gray-600 mb-4">
          本サービスの利用を停止し、データを削除する
        </p>

        <button
          onClick={() => setShowDialog(true)}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-md transition-colors"
        >
          利用を停止する
        </button>

        <Alert
          type="error"
          message="利用を停止すると、サービス上のすべてのデータが完全に削除されます。この操作は取り消すことができませんので注意してください。"
          className="mt-4"
        />
      </section>

      {/* 確認ダイアログ */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              本当に利用を停止しますか?
            </h3>

            <div className="text-sm text-gray-700 mb-4 space-y-2">
              <p>この操作を実行すると、以下の処理が行われます:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>サービス上のすべてのデータが完全に削除されます</li>
                <li>作成されたBCPやコメントがすべて削除されます</li>
                <li>アカウント情報が削除されます</li>
              </ul>
            </div>

            <Alert
              type="error"
              message="削除されたデータは復旧できません。この操作は取り消すことができませんので、十分ご注意ください。"
              className="mb-6"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowDialog(false)}
                disabled={isDeleting}
                className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md border border-gray-300 transition-colors disabled:opacity-50"
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
              >
                {isDeleting ? '削除中...' : '利用を停止する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
